import { useEffect, useRef, useState } from 'react';
import { useIsMobile } from './useUserAgent';

/**
 * Shared redirect logic for the Done page.
 * Auto-redirects after 3s unless the wallet handles it on mobile (same-device).
 * On desktop (cross-device), always redirect — the wallet redirect on the
 * phone does not affect the desktop browser.
 */
export function useDoneRedirect(
  onComplete: () => Promise<void>,
  continueInApp: boolean,
  deliveryMode: 'redirect' | 'webhook',
  delayMs = 3000,
) {
  const isMobile = useIsMobile();
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (continueInApp && isMobile) return;
    if (attempted.current) return;
    attempted.current = true;

    // Webhook mode: call onComplete immediately (triggers OAuth update + postMessage)
    if (deliveryMode === 'webhook') {
      setCompleting(true);
      onComplete().catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setCompleting(false);
      });
      return;
    }

    const timer = setTimeout(async () => {
      setCompleting(true);
      try {
        await onComplete();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setCompleting(false);
      }
    }, delayMs);
    return () => clearTimeout(timer);
  }, [onComplete, continueInApp, deliveryMode, isMobile, delayMs]);

  const handleManualComplete = async () => {
    setCompleting(true);
    setError(null);
    try {
      await onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setCompleting(false);
    }
  };

  return { error, completing, handleManualComplete };
}
